import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * useUserPreferences Hook
 * 
 * Manages user preferences including conversation mode, audio mode,
 * leader board visibility, quest pack selector type, and intro preference.
 * Handles loading and saving preferences to the API. Extracted from ChatApp component.
 */
export const useUserPreferences = ({ authenticatedUser }) => {
  const authToken = authenticatedUser?.token;

  // Preference state
  const [userPreferences, setUserPreferences] = useState(null);
  const [conversationMode, setConversationMode] = useState("voiceText");
  const [audioMode, setAudioMode] = useState("pushToTalk");
  const [showLeaderBoard, setShowLeaderBoard] = useState(true);
  const [useScrollableQuestPackSelector, setUseScrollableQuestPackSelector] = useState(false);
  const [showIntroPreference, setShowIntroPreference] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [godMode, setGodMode] = useState(false);
  const [hasLoadedInitialPreferences, setHasLoadedInitialPreferences] = useState(false);
  const [shouldShowIntroOnLoad, setShouldShowIntroOnLoad] = useState(false);

  // Fetch user preferences from API
  const fetchUserPreferences = useCallback(async () => {
    if (!authToken) return;

    try {
      const response = await fetch('/.netlify/functions/get-user-preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const data = await response.json();
      setUserPreferences(data);
      return data;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      toast.error('Failed to load user preferences.');
      throw error;
    }
  }, [authToken]);

  // Update user preferences via API
  const updateUserPreferences = async (customPreferences = null) => {
    try {
      const token = authenticatedUser?.token;

      // Use provided preferences or current state
      const preferencesToSave = customPreferences || {
        conversation_mode: conversationMode,
        audio_mode: audioMode,
        show_intro: showIntroPreference,
        custom_settings: {
          show_leader_board: showLeaderBoard,
          use_scrollable_quest_pack_selector: useScrollableQuestPackSelector,
          god_mode: godMode,
        }
      };

      console.log("Sending user preferences payload:", preferencesToSave);

      const response = await fetch('/.netlify/functions/update-user-preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(preferencesToSave),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Server error response:", errorData);
        throw new Error(`Error ${response.status}: ${errorData.message || 'Unknown error'}`);
      }

      const data = await response.json();
      console.log("Updated preferences:", data);
      toast.success("Preferences updated!");
      return data;
    } catch (error) {
      console.error("Error updating preferences:", error);
      toast.error(`Failed to update preferences: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Apply preferences to state
  const applyPreferences = useCallback((preferences) => {
    if (!preferences) return;

    const customSettings = preferences.custom_settings || {};

    if (preferences.conversation_mode) {
      setConversationMode(preferences.conversation_mode);
    }
    if (preferences.audio_mode) {
      setAudioMode(preferences.audio_mode);
    }
    const showLeaderBoardPreference = preferences.show_leader_board ?? customSettings.show_leader_board;
    if (showLeaderBoardPreference !== undefined) {
      setShowLeaderBoard(showLeaderBoardPreference);
    }
    const useScrollableSelectorPreference = preferences.use_scrollable_quest_pack_selector ?? customSettings.use_scrollable_quest_pack_selector;
    if (useScrollableSelectorPreference !== undefined) {
      setUseScrollableQuestPackSelector(useScrollableSelectorPreference);
    }
    if (preferences.show_intro !== undefined) {
      setShowIntroPreference(preferences.show_intro);
      // Set shouldShowIntroOnLoad to true only on initial load if show_intro is true
      setShouldShowIntroOnLoad(preferences.show_intro);
    }
    if (preferences.is_admin !== undefined) {
      setIsAdmin(Boolean(preferences.is_admin));
    }

    const godModePreference = preferences.god_mode ?? customSettings.god_mode;
    if (godModePreference !== undefined) {
      setGodMode(Boolean(godModePreference));
    }

    // Mark that we've loaded initial preferences
    setHasLoadedInitialPreferences(true);
  }, []);

  // Load preferences on mount when authenticated user is available
  useEffect(() => {
    const loadPreferences = async () => {
      if (authToken) {
        try {
          const preferences = await fetchUserPreferences();
          applyPreferences(preferences);
        } catch (error) {
          // Error already handled in fetchUserPreferences
          setHasLoadedInitialPreferences(true); // Still mark as loaded to prevent infinite loading
        }
      }
    };

    loadPreferences();
  }, [authToken, fetchUserPreferences, applyPreferences]);

  // Update show_intro preference specifically (used for intro hiding)
  const updateShowIntroPreference = async (showIntro) => {
    const preferences = {
      conversation_mode: conversationMode,
      audio_mode: audioMode,
      show_intro: showIntro,
      custom_settings: {
        show_leader_board: showLeaderBoard,
        use_scrollable_quest_pack_selector: useScrollableQuestPackSelector,
        god_mode: godMode,
      }
    };

    try {
      await updateUserPreferences(preferences);
      setShowIntroPreference(showIntro);
      setShouldShowIntroOnLoad(showIntro);
    } catch (error) {
      console.error("Error updating show_intro preference:", error);
    }
  };

  return {
    // State
    userPreferences,
    conversationMode,
    audioMode,
    showLeaderBoard,
    useScrollableQuestPackSelector,
    showIntroPreference,
    isAdmin,
    godMode,
    hasLoadedInitialPreferences,
    shouldShowIntroOnLoad,
    
    // Actions
    setConversationMode,
    setAudioMode,
    setShowLeaderBoard,
    setUseScrollableQuestPackSelector,
    setShowIntroPreference,
    setShouldShowIntroOnLoad,
    setGodMode,
    updateUserPreferences,
    updateShowIntroPreference,
    fetchUserPreferences,
    
    // Utilities
    applyPreferences
  };
};

export default useUserPreferences;
